import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import api from '../services/api';

const ACTIONS = {
  SET_LOADING: 'SET_LOADING', SET_ERROR: 'SET_ERROR', CLEAR_ERROR: 'CLEAR_ERROR',
  QUEUE_JOINED: 'QUEUE_JOINED', QUEUE_UPDATE: 'QUEUE_UPDATE',
  QUEUE_LEFT: 'QUEUE_LEFT', QUEUE_TIMEOUT: 'QUEUE_TIMEOUT',
  ROOM_JOINED: 'ROOM_JOINED',
  PARTICIPANT_JOINED: 'PARTICIPANT_JOINED', PARTICIPANT_LEFT: 'PARTICIPANT_LEFT',
  PARTICIPANT_UPDATE: 'PARTICIPANT_UPDATE',
  SESSION_STARTED: 'SESSION_STARTED', TIMER_UPDATE: 'TIMER_UPDATE',
  SESSION_ENDED: 'SESSION_ENDED', SESSION_SUMMARY: 'SESSION_SUMMARY',
  CHAT_MESSAGE: 'CHAT_MESSAGE', CHAT_REACTION: 'CHAT_REACTION', CHAT_ERROR: 'CHAT_ERROR',
  SET_USAGE: 'SET_USAGE', SET_HISTORY: 'SET_HISTORY',
  RESET_ROOM: 'RESET_ROOM',
};

export const VIEWS = { HOME: 'home', QUEUE: 'queue', LOBBY: 'lobby', SESSION: 'session', SUMMARY: 'summary' };

export const QUICK_REACTIONS = [
  { key: 'lets_go', label: "Let's Go!", emoji: '\u{1F680}' },
  { key: 'nice_streak', label: 'Nice Streak!', emoji: '\u{1F525}' },
  { key: 'almost_there', label: 'Almost There!', emoji: '\u{1F4AA}' },
  { key: 'you_got_this', label: 'You Got This!', emoji: '\u{2B50}' },
  { key: 'focus_time', label: 'Focus Time', emoji: '\u{1F3AF}' },
  { key: 'taking_break', label: 'Taking a Break', emoji: '\u{2615}' },
];

var initialState = {
  view: VIEWS.HOME, loading: false, error: null,
  inQueue: false, queueStartTime: null,
  room: null, roomCode: null, participants: [],
  sessionActive: false, timeRemaining: 0, totalDuration: 0,
  messages: [], activityStats: { questionsAnswered: 0, correctAnswers: 0 },
  sessionSummary: null,
  usage: { sessionsUsed: 0, sessionsLimit: 3, canStart: true },
  history: [],
};

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING: return { ...state, loading: action.payload };
    case ACTIONS.SET_ERROR: return { ...state, error: action.payload, loading: false };
    case ACTIONS.CLEAR_ERROR: return { ...state, error: null };
    case ACTIONS.QUEUE_JOINED:
      return { ...state, inQueue: true, queueStartTime: Date.now(), view: VIEWS.QUEUE, loading: false };
    case ACTIONS.QUEUE_UPDATE:
      return state;
    case ACTIONS.QUEUE_LEFT:
      return { ...state, inQueue: false, queueStartTime: null, view: VIEWS.HOME, loading: false };
    case ACTIONS.QUEUE_TIMEOUT:
      return { ...state, inQueue: false, queueStartTime: null, view: VIEWS.HOME, error: 'No study partners found. Try again or create a room.', loading: false };
    case ACTIONS.ROOM_JOINED:
      var isActiveSession = action.payload.status === 'active';
      return { ...state, room: action.payload, roomCode: action.payload.roomCode, participants: action.payload.participants || [], view: isActiveSession ? VIEWS.SESSION : VIEWS.LOBBY, inQueue: false, loading: false, sessionActive: isActiveSession ? true : state.sessionActive, timeRemaining: isActiveSession ? (action.payload.remainingSeconds || action.payload.duration || 0) : state.timeRemaining, totalDuration: isActiveSession ? (action.payload.duration || state.totalDuration) : state.totalDuration };
    case ACTIONS.PARTICIPANT_JOINED:
      return { ...state, participants: [...state.participants.filter(function(p) { return p.userId !== action.payload.userId; }), action.payload] };
    case ACTIONS.PARTICIPANT_LEFT:
      return { ...state, participants: state.participants.filter(function(p) { return p.userId !== action.payload.userId; }) };
    case ACTIONS.PARTICIPANT_UPDATE:
      return { ...state, participants: state.participants.map(function(p) { return p.userId === action.payload.userId ? { ...p, ...action.payload } : p; }) };
    case ACTIONS.SESSION_STARTED:
      return { ...state, sessionActive: true, timeRemaining: action.payload.duration || action.payload.durationSeconds || 1500, totalDuration: action.payload.duration || action.payload.durationSeconds || 1500, view: VIEWS.SESSION, messages: [], activityStats: { questionsAnswered: 0, correctAnswers: 0 } };
    case ACTIONS.TIMER_UPDATE:
      return { ...state, timeRemaining: action.payload.remaining };
    case ACTIONS.SESSION_ENDED:
      return { ...state, sessionActive: false, timeRemaining: 0 };
    case ACTIONS.SESSION_SUMMARY:
      return { ...state, sessionSummary: action.payload, view: VIEWS.SUMMARY, sessionActive: false };
    case ACTIONS.CHAT_MESSAGE:
      return { ...state, messages: [...state.messages.slice(-99), action.payload] };
    case ACTIONS.CHAT_REACTION:
      return { ...state, messages: [...state.messages.slice(-99), { ...action.payload, isReaction: true }] };
    case ACTIONS.CHAT_ERROR:
      return { ...state, error: action.payload };
    case ACTIONS.SET_USAGE:
      return { ...state, usage: action.payload };
    case ACTIONS.SET_HISTORY:
      return { ...state, history: action.payload };
    case ACTIONS.RESET_ROOM:
      return { ...initialState, usage: state.usage, history: state.history };
    default: return state;
  }
}

var StudyRoomContext = createContext(null);

export function StudyRoomProvider({ children }) {
  var [state, dispatch] = useReducer(reducer, initialState);
  var { user } = useAuth();
  var token = localStorage.getItem('token');
  var { hasAccess } = useSubscription();
  var socketRef = useRef(null);
  var timerRef = useRef(null);
  var inQueueRef = useRef(false);

  // Keep inQueueRef synced
  useEffect(function() { inQueueRef.current = state.inQueue; }, [state.inQueue]);

  // Socket connection
  useEffect(function() {
    if (!token || !user) {
return;
    }
var socket = io(window.location.origin, {
      auth: { token: token },
      forceNew: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', function() {
socket.emit('authenticate', user._id || user.userId);
    });

    socket.on('disconnect', function(reason) {
});

    socket.on('connect_error', function(err) {
});

    // --- BACKEND EVENT LISTENERS (underscore naming) ---

    // Queue status (backend uses single event for all queue states)
    socket.on('studyroom:queue_status', function(d) {
if (d.estimatedWait === 'left_queue') {
        dispatch({ type: ACTIONS.QUEUE_LEFT });
      } else if (d.estimatedWait && typeof d.estimatedWait === 'string' && d.estimatedWait.includes('Timeout')) {
        dispatch({ type: ACTIONS.QUEUE_TIMEOUT });
      } else if (inQueueRef.current) {
        dispatch({ type: ACTIONS.QUEUE_UPDATE, payload: d });
      } else {
        dispatch({ type: ACTIONS.QUEUE_JOINED, payload: d });
      }
    });

    // Matched + room joined
    socket.on('studyroom:matched', function(d) {
dispatch({ type: ACTIONS.ROOM_JOINED, payload: d });
    });

    socket.on('studyroom:room_joined', function(d) {
dispatch({ type: ACTIONS.ROOM_JOINED, payload: d });
    });

    // Participants
    socket.on('studyroom:participant_joined', function(d) {
dispatch({ type: ACTIONS.PARTICIPANT_JOINED, payload: d });
    });

    socket.on('studyroom:participant_left', function(d) {
dispatch({ type: ACTIONS.PARTICIPANT_LEFT, payload: d });
    });

    // Session lifecycle
    socket.on('studyroom:session_started', function(d) {
dispatch({ type: ACTIONS.SESSION_STARTED, payload: d });
    });

    socket.on('studyroom:timer_sync', function(d) {
      dispatch({ type: ACTIONS.TIMER_UPDATE, payload: { remaining: d.remainingSeconds } });
    });

    socket.on('studyroom:session_complete', function(d) {
dispatch({ type: ACTIONS.SESSION_ENDED });
      dispatch({ type: ACTIONS.SESSION_SUMMARY, payload: d.summary || d });
    });

    socket.on('studyroom:halfway', function() {});
    socket.on('studyroom:session_ending', function() {});

    // Chat
    socket.on('studyroom:chat_received', function(d) {
      dispatch({ type: ACTIONS.CHAT_MESSAGE, payload: d });
    });

    socket.on('studyroom:reaction_received', function(d) {
      dispatch({ type: ACTIONS.CHAT_REACTION, payload: d });
    });

    socket.on('studyroom:chat_blocked', function(d) {
      dispatch({ type: ACTIONS.CHAT_ERROR, payload: d.reason || 'Message not sent' });
    });

    // Activity
    socket.on('studyroom:activity_broadcast', function(d) {
      dispatch({ type: ACTIONS.PARTICIPANT_UPDATE, payload: d });
    });

    // Error
    socket.on('studyroom:error', function(d) {
dispatch({ type: ACTIONS.SET_ERROR, payload: d.message || 'Something went wrong' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    });

    // Report confirmation
    socket.on('studyroom:user_reported', function(d) {
});

    return function() {
socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, user]);

  // Client-side timer countdown
  useEffect(function() {
    if (state.sessionActive && state.timeRemaining > 0) {
      timerRef.current = setInterval(function() {
        dispatch({ type: ACTIONS.TIMER_UPDATE, payload: { remaining: Math.max(0, state.timeRemaining - 1) } });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return function() { clearInterval(timerRef.current); };
  }, [state.sessionActive, state.timeRemaining]);

  // Heartbeat during active sessions
  useEffect(function() {
    if (!state.sessionActive || !socketRef.current || !state.room) return;
    var hb = setInterval(function() {
      if (socketRef.current && state.room) {
        socketRef.current.emit('studyroom:heartbeat', { roomId: state.room.roomId });
      }
    }, 30000);
    return function() { clearInterval(hb); };
  }, [state.sessionActive, state.room]);

  // --- ACTIONS (emit with underscore naming) ---

  var joinQueue = useCallback(function(opts) {
    opts = opts || {};
    var s = socketRef.current;
if (!s) return;
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    s.emit('studyroom:join_queue', {
      grade: opts.grade || (user && user.profile && user.profile.grade) || 10,
      subject: opts.subject || 'Mathematics',
      topic: opts.topic || null,
      duration: opts.duration || 1500,
    });
    // Optimistic: show queue view immediately
    dispatch({ type: ACTIONS.QUEUE_JOINED });
  }, [user]);

  var leaveQueue = useCallback(function() {
    var s = socketRef.current;
    if (!s) return;
    s.emit('studyroom:leave_queue');
    dispatch({ type: ACTIONS.QUEUE_LEFT });
  }, []);

  var createRoom = useCallback(function(opts) {
    opts = opts || {};
    var s = socketRef.current;
if (!s) return;
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    s.emit('studyroom:create_room', {
      grade: opts.grade || (user && user.profile && user.profile.grade) || 10,
      subject: opts.subject || 'Mathematics',
      duration: opts.duration || 1500,
      maxParticipants: opts.maxParticipants || 2,
    });
  }, [user]);

  var joinRoom = useCallback(function(code) {
    var s = socketRef.current;
    if (!s || !code) return;
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    s.emit('studyroom:join_room', { roomCode: code.toUpperCase() });
  }, []);

  var startSession = useCallback(function() {
    var s = socketRef.current;
    if (!s || !state.room) return;
    s.emit('studyroom:start_session', { roomId: state.room.roomId });
  }, [state.room]);

  var leaveRoom = useCallback(function() {
    var s = socketRef.current;
    if (!s) return;
    if (state.room) {
      s.emit('studyroom:leave_room', { roomId: state.room.roomId });
    }
    dispatch({ type: ACTIONS.RESET_ROOM });
  }, [state.room]);

  var sendMessage = useCallback(function(msg) {
    var s = socketRef.current;
    if (!s || !state.room || !msg || !msg.trim()) return;
    s.emit('studyroom:chat_message', { roomId: state.room.roomId, message: msg.trim() });
  }, [state.room]);

  var sendReaction = useCallback(function(key) {
    var s = socketRef.current;
    if (!s || !state.room) return;
    s.emit('studyroom:quick_reaction', { roomId: state.room.roomId, reactionType: key });
  }, [state.room]);

  var updateActivity = useCallback(function(data) {
    var s = socketRef.current;
    if (!s || !state.room) return;
    s.emit('studyroom:activity_update', { roomId: state.room.roomId, ...data });
  }, [state.room]);

  // --- REST API ---

  var fetchStatus = useCallback(async function() {
    try {
      var res = await api.get('/api/study-rooms/status');
      var d = res.data.data || res.data;
      if (d.usage) dispatch({ type: ACTIONS.SET_USAGE, payload: d.usage });
      return d;
    } catch (e) { return null; }
  }, []);

  var fetchHistory = useCallback(async function(page, limit) {
    try {
      var res = await api.get('/api/study-rooms/history?page=' + (page || 1) + '&limit=' + (limit || 10));
      var d = res.data.data || res.data;
      dispatch({ type: ACTIONS.SET_HISTORY, payload: d.sessions || d });
      return d;
    } catch (e) { return null; }
  }, []);

  var fetchStats = useCallback(async function() {
    try { var res = await api.get('/api/study-rooms/stats'); return res.data.data || res.data; }
    catch (e) { return null; }
  }, []);

  var submitReport = useCallback(async function(data) {
    var res = await api.post('/api/study-rooms/report', data);
    return res.data.data || res.data;
  }, []);

  var clearError = useCallback(function() { dispatch({ type: ACTIONS.CLEAR_ERROR }); }, []);
  var resetRoom = useCallback(function() { dispatch({ type: ACTIONS.RESET_ROOM }); }, []);

  var value = {
    ...state,
    joinQueue: joinQueue, leaveQueue: leaveQueue, createRoom: createRoom, joinRoom: joinRoom,
    startSession: startSession, leaveRoom: leaveRoom, sendMessage: sendMessage,
    sendReaction: sendReaction, updateActivity: updateActivity,
    fetchStatus: fetchStatus, fetchHistory: fetchHistory, fetchStats: fetchStats,
    submitReport: submitReport, clearError: clearError, resetRoom: resetRoom,
    hasAccess: hasAccess, isPro: hasAccess('study_rooms_unlimited'),
  };

  return React.createElement(StudyRoomContext.Provider, { value: value }, children);
}

export function useStudyRoom() {
  var ctx = useContext(StudyRoomContext);
  if (!ctx) throw new Error('useStudyRoom must be used within StudyRoomProvider');
  return ctx;
}

export default StudyRoomContext;
